import React from 'react';
import { isEarningsFilename } from '@utils/usefulFuncs';
import { earningsDocTypes } from '@constants';
import { fetchCompanyDocs, fetchFedDocs, fetchNewsCompanyDocs, fetchTickersDocs, fetchUserDocs } from './useDocsCache';

const initialState = {
  isLoading: true,
};

const ACTIONS = {
  update: 'u',
  startLoading: 'sl',
  error: 'err',
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.startLoading: {
      return {
        ...state,
        docs: [],
        isLoading: true,
      };
    }
    case ACTIONS.update: {
      const { data } = action.payload;

      return {
        ...state,
        isLoading: false,
        docs: data,
      };
    }
    case ACTIONS.error: {
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    }

    default:
      throw new Error('missing case: useDocs');
  }
}

function filterDocs(docs, filters) {
  if (!filters) return docs;
  if (filters.docMode === 'earnings') {
    return docs.filter((d) => {
      return earningsDocTypes.includes(d.doc_type) || isEarningsFilename(d.filename);
    });
  }
  return docs;
}

export const useTickerDocs = (pFilters, onAfterDocReceived) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  const onCompanyChange = async (filters) => {
    dispatch({
      type: ACTIONS.startLoading,
    });
    const docs = await fetchTickersDocs(filters);
    dispatch({
      type: ACTIONS.update,
      payload: {
        data: docs,
      },
    });
    onAfterDocReceived && onAfterDocReceived(docs);
  };

  React.useEffect(() => {
    onCompanyChange(pFilters);
  }, [pFilters]);

  return state;
};

export const useCompanyDocs = (company, filters, onAfterDocReceived) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  React.useEffect(() => {
    const onCompanyChange = async () => {
      const filters = {};
      dispatch({
        type: ACTIONS.startLoading,
      });
      const docs = await fetchCompanyDocs(company, filters);
      dispatch({
        type: ACTIONS.update,
        payload: {
          data: docs,
        },
      });
      onAfterDocReceived && onAfterDocReceived(docs);
    };
    if (company) {
      onCompanyChange(company);
    }
  }, [company, filters]);

  return state;
};

export const useNewsCompanyDocs = (company, filters, onAfterDocReceived) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  React.useEffect(() => {
    const onCompanyChange = async () => {
      const filters = {};
      dispatch({
        type: ACTIONS.startLoading,
      });
      const docs = await fetchNewsCompanyDocs(company, filters);
      dispatch({
        type: ACTIONS.update,
        payload: {
          data: docs,
        },
      });
      onAfterDocReceived && onAfterDocReceived(docs);
    };
    onCompanyChange();
  }, [company, filters]);

  return state;
};

export const useUserUploadedDocs = (filters, onAfterDocReceived) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  const onFetchDocs = async (filters) => {
    dispatch({
      type: ACTIONS.startLoading,
    });
    const docs = await fetchUserDocs(filters);
    const filteredDocs = filterDocs(docs, filters);
    dispatch({
      type: ACTIONS.update,
      payload: {
        data: filteredDocs,
      },
    });
    onAfterDocReceived && onAfterDocReceived(filteredDocs);
  };

  React.useEffect(() => {
    onFetchDocs(filters);
  }, [filters]);

  return React.useMemo(() => ({ ...state, onRefreshDocs: onFetchDocs }), [state]);
};

export const useFedDocs = (onAfterDocReceived) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  const onFetchDocs = async () => {
    const filters = {};
    dispatch({
      type: ACTIONS.startLoading,
    });
    const docs = await fetchFedDocs(filters);
    dispatch({
      type: ACTIONS.update,
      payload: {
        data: docs,
      },
    });
    onAfterDocReceived && onAfterDocReceived(docs);
  };

  React.useEffect(() => {
    onFetchDocs();
  }, []);

  return state;
};
